import type { ClaimTariffStatusValue } from '@billing/validation/tariff';
import type { ReconciliationComments } from '@billing/validation';
//MRT
import { MRT_ColumnFiltersState, MRT_SortingState } from 'mantine-react-table';

// React Query
import {
  UseMutationResult,
  UseQueryResult,
  QueryClient,
  useQuery,
} from '@tanstack/react-query';
// Http Methods
import { getRequest as get } from '../utils/HttpMethods';
import queryString from '../utils/queryStringBuilder';

// Interfaces
import { Claim, type CreateClaimRequest, isClaim } from 'interfaces/claim';
import { ClaimHistory, isClaimHistory } from 'interfaces/claimHistory';
import {
  ClaimBatchDetails,
  ListClaimBatchesResponse,
} from 'interfaces/claimBatch';

//Utils
import {
  convertClaimFiltersToQueryString,
  convertGlobalFilterToQueryString,
  convertSortingToQueryString,
} from 'utils/filterUtils';

import {
  CountParams,
  CountReturnTypes,
  OrganizationClaimsCounts,
  PaginatedResult,
  PaginationParams,
  StatusesCountResult,
  useCountOrganizationClaimsQuery,
  useCountQuery,
  hasSiteID,
  useGetQuery,
  useListQuery,
  useMutationQuery,
  usePaginatedQuery,
  prefetchQuery,
} from 'api/utils/queries';
import {
  claimListSchema,
  isClaimList,
  type BulkClaimEditInput,
  type ClaimList,
} from '@billing/validation';
import type {
  ClaimRuleEvaluationInput,
  RuleEvaluationResult,
} from '@billing/validation/rules';
import { NoDataFoundError } from 'api/utils/APIRequestError';
import { handleSentryErrors } from 'api/utils/HandleSentryErrors';
import { postRequest as post, putRequest as put } from 'api/utils/HttpMethods';

export interface ClaimTotalsSummary {
  payorTariffCounts?: Record<string, number>;
  totalAmountSubmitted?: number;
  totalAmountPaid?: number;
  totalAmountOutstanding?: number;
}

export type ClaimWithRulesResult = Claim & {
  rules: RuleEvaluationResult;
};

type ClaimMutationResponse = {
  claim: Claim;
  rules: RuleEvaluationResult;
};

export type ClaimMutationResult = Claim | ClaimWithRulesResult;

type BulkClaimMutationResponse = {
  claims: Claim[];
  rules: RuleEvaluationResult;
};

export type BulkClaimMutationResult = BulkClaimMutationResponse;

const isClaimMutationResponse = (
  response: ClaimMutationResponse | Claim
): response is ClaimMutationResponse =>
  Boolean(
    response &&
      typeof response === 'object' &&
      'claim' in response &&
      'rules' in response &&
      (response as ClaimMutationResponse).claim
  );

const isBulkClaimMutationResponse = (
  response: unknown
): response is BulkClaimMutationResponse =>
  Boolean(
    response &&
      typeof response === 'object' &&
      !Array.isArray(response) &&
      'claims' in response &&
      'rules' in response &&
      Array.isArray((response as BulkClaimMutationResponse).claims)
  );

export type ClaimPaginatedResult = PaginatedResult<ClaimList> &
  ClaimTotalsSummary;

export const claimQueryKeys = {
  all: ['claim'],
  get: (id: string) => [...claimQueryKeys.all, id],
  getClaimHistory: (claimID: string) => [
    ...claimQueryKeys.all,
    'claimHistory',
    claimID,
  ],
  getPendingTariffResubmissions: (claimID: string) => [
    ...claimQueryKeys.all,
    'tariffResubmission',
    'pending',
    claimID,
  ],
  getSiteClaimStatusesCount: (
    siteID: string,
    filters?: MRT_ColumnFiltersState
  ) => [...claimQueryKeys.all, 'siteClaimStatusesCount', siteID, filters],
  getUserClaimStatusesCount: (
    userID: string,
    siteID: string,
    filters?: MRT_ColumnFiltersState
  ) => [
    ...claimQueryKeys.all,
    'userClaimStatusesCount',
    userID,
    siteID,
    filters,
  ],
  getSiteClaimTariffStatusesCount: (siteID: string) => [
    ...claimQueryKeys.all,
    'siteClaimTariffStatusesCount',
    siteID,
  ],
  getSiteClaimTariffStatusesCountWithFilters: (
    siteID: string,
    filters?: MRT_ColumnFiltersState,
    globalFilter?: string
  ) => [
    ...claimQueryKeys.all,
    'siteClaimTariffStatusesCount',
    siteID,
    filters,
    globalFilter,
  ],
  getUserClaimTariffStatusesCount: (
    userID: string,
    siteID: string | string[]
  ) => [...claimQueryKeys.all, 'userClaimTariffStatusesCount', userID, siteID],
  getUserClaimTariffStatusesCountWithFilters: (
    userID: string,
    siteID: string | string[],
    filters?: MRT_ColumnFiltersState,
    globalFilter?: string
  ) => [
    ...claimQueryKeys.all,
    'userClaimTariffStatusesCount',
    userID,
    siteID,
    filters,
    globalFilter,
  ],
  getSiteClaimStatusCount: (
    siteID: string,
    tariffStatus: ClaimTariffStatusValue,
    filters: MRT_ColumnFiltersState
  ) => [
    ...claimQueryKeys.all,
    'siteClaimStatusCount',
    siteID,
    tariffStatus,
    filters,
  ],
  list: () => [...claimQueryKeys.all, 'list'],
  listBySite: (siteID: string) => [...claimQueryKeys.all, siteID],
  listClaimsByOrganizationSitesInRange: (
    sites: string[],
    startDate: string,
    endDate: string
  ) => [...claimQueryKeys.all, sites, startDate, endDate],
  listSiteClaimsByStatus: (siteID: string, status: string) => [
    ...claimQueryKeys.all,
    siteID,
    status,
  ],
  listAllClaims: (isAdmin: boolean, siteID: string) => [
    ...claimQueryKeys.all,
    'all',
    isAdmin,
    siteID,
  ],
  listStaleClaims: (siteID: string) => [...claimQueryKeys.all, 'all', siteID],
  paginatedClaimsBySite: (
    siteID: string,
    page: number,
    pageSize: number,
    filters: MRT_ColumnFiltersState,
    globalFilter: string,
    sorting: MRT_SortingState
  ) => [
    ...claimQueryKeys.all,
    siteID,
    page,
    pageSize,
    filters,
    globalFilter,
    sorting,
  ],
  paginatedClaimsByUser: (
    userID: string,
    siteID: string | string[],
    page: number,
    pageSize: number,
    filters: MRT_ColumnFiltersState,
    globalFilter: string,
    sorting: MRT_SortingState
  ) => [
    ...claimQueryKeys.all,
    userID,
    siteID,
    page,
    pageSize,
    filters,
    globalFilter,
    sorting,
  ],
  paginatedClaimsByPatient: (
    patientID: string,
    siteID: string,
    page: number,
    pageSize: number,
    filters: MRT_ColumnFiltersState,
    globalFilter: string,
    sorting: MRT_SortingState,
    timezone
  ) => [
    ...claimQueryKeys.all,
    patientID,
    siteID,
    page,
    pageSize,
    filters,
    globalFilter,
    sorting,
    timezone,
  ],
  bulkCreateClaims: (siteID: string) => [
    ...claimQueryKeys.all,
    'bulkCreateClaims',
    siteID,
  ],
  providerHospitalCodes: (providerID: string, siteID?: string) => [
    ...claimQueryKeys.all,
    'providerHospitalCodes',
    providerID,
    siteID,
  ],
  csvClaims: (siteID: string, status?: string) => [
    ...claimQueryKeys.all,
    'csvClaims',
    siteID,
    status,
  ],
  bulkReconcile: () => [...claimQueryKeys.all, 'bulkReconcile'],
  previousBatches: (siteID: string, page: number, pageSize: number) => [
    ...claimQueryKeys.all,
    'previousBatches',
    siteID,
    page,
    pageSize,
  ],
  previousBatchDetails: (siteID: string, batchID: string) => [
    ...claimQueryKeys.all,
    'previousBatchDetails',
    siteID,
    batchID,
  ],
};
import { tariffResubmissionRequestSchema } from '@billing/validation/tariffResubmissionRequest';
import { z } from 'zod';

const getClaim = async (id: string): Promise<Claim | null> =>
  await get<Claim>('QBilling', `/resource/claim${queryString({ id })}`);

/**
 * @description Fetches the count of claim statuses for a specific site.
 * @param siteID - The ID of the site.
 * @param filters - Optional filters to apply.
 * @returns A promise that resolves to a `StatusesCountResult` object.
 */
const getSiteClaimStatusesCount = async ({
  siteID,
}: CountParams): Promise<StatusesCountResult> => {
  const data = await get<StatusesCountResult>(
    'QBilling',
    `/resource/claim/status/count${queryString({
      siteID: siteID,
    })}`
  );
  return data;
};

/**
 * @description Fetches the count of claim tariff statuses for a specific site.
 * @param siteID - The ID of the site.
 * @returns A promise that resolves to a `StatusesCountResult` object.
 */
const getSiteClaimTariffStatusesCount = async ({
  siteID,
  filters,
  globalFilter,
}: CountParams): Promise<StatusesCountResult> => {
  const filtersString = convertClaimFiltersToQueryString(filters);
  const globalFilterString = convertGlobalFilterToQueryString(globalFilter);
  const data = await get<StatusesCountResult>(
    'QBilling',
    `/resource/claim/tariff/status/count${queryString({
      siteID,
      filters: encodeURIComponent(filtersString),
      globalFilter: encodeURIComponent(globalFilterString),
    })}`
  );
  return data;
};

/**
 * @description Fetches the count of claim statuses for a specific user.
 * @param userID - The ID of the user.
 * @param filters - Optional filters to apply.
 * @returns A promise that resolves to a `StatusesCountResult` object.
 */
const getUserClaimStatusesCount = async ({
  userID,
  siteID,
  filters,
}: {
  userID: string;
  siteID: string;
  filters?: MRT_ColumnFiltersState;
}): Promise<StatusesCountResult> => {
  const filtersString = convertClaimFiltersToQueryString(filters);
  const data = await get<StatusesCountResult>(
    'QBilling',
    `/resource/claim/user/status/count${queryString({
      userID,
      siteID,
      filters: encodeURIComponent(filtersString),
    })}`
  );
  return data;
};

/**
 * @description Fetches the count of claim tariff statuses for a specific user and site.
 * @param userID - The ID of the user.
 * @param siteID - The ID of the site.
 * @returns A promise that resolves to a `StatusesCountResult` object.
 */
const getUserClaimTariffStatusesCount = async ({
  userID,
  siteID,
  filters,
  globalFilter,
}: {
  userID: string;
  siteID: string | string[];
  filters?: MRT_ColumnFiltersState;
  globalFilter?: string;
}): Promise<StatusesCountResult> => {
  const filtersString = convertClaimFiltersToQueryString(filters);
  const globalFilterString = convertGlobalFilterToQueryString(globalFilter);
  const data = await get<StatusesCountResult>(
    'QBilling',
    `/resource/claim/user/tariff/status/count${queryString({
      userID,
      siteID,
      filters: encodeURIComponent(filtersString),
      globalFilter: encodeURIComponent(globalFilterString),
    })}`
  );
  return data;
};

/**
 * @description Fetches the count of a specific claim status for a site.
 * @param siteID - The ID of the site.
 * @param tariffStatus - The tariff status to count.
 * @param filters - Optional filters to apply.
 * @returns A promise that resolves to a `StatusesCountResult` object.
 * @returns [key: string]: number
 */
const getSiteClaimStatusCount = async ({
  siteID,
  tariffStatus,
  filters,
}: CountParams): Promise<StatusesCountResult> => {
  const filtersString = convertClaimFiltersToQueryString(filters);
  const data = await get<StatusesCountResult>(
    `QBilling`,
    `/resource/claim/site/status/count${queryString({
      siteID,
      tariffStatus,
      filters: encodeURIComponent(filtersString),
    })}`
  );
  return data;
};

/**
 * @description Fetches the history of a claim by its ID.
 * @param claimID - The ID of the claim.
 * @returns A promise that resolves to an array of `ClaimHistory` objects.
 */
const getClaimHistory = async (claimID: string): Promise<ClaimHistory[]> =>
  await get<ClaimHistory[]>(
    'QBilling',
    `/resource/claim/history${queryString({ claimID })}`
  );

/**
 * @description Fetches all claims.
 * @returns A promise that resolves to an array of `Claim` objects.
 */
const listClaims = async (): Promise<Claim[]> =>
  await get<Claim[]>('QBilling', `/resource/claim/all`);

type TariffResubmissionRequest = z.infer<
  typeof tariffResubmissionRequestSchema
>;

const listPendingTariffResubmissions = async ({
  claimID,
}: {
  claimID: string;
}): Promise<TariffResubmissionRequest[]> => {
  return await get<TariffResubmissionRequest[]>(
    'QBilling',
    `/resource/claim/tariff/resubmission/pending${queryString({ claimID })}`
  );
};

/**
 * @description Fetches claims that have been ready longer than 6 months by site ID.
 * @param siteID - The ID of the site the claims belong to.
 * @returns A promise that resolves to an array of `Claim` objects.
 */
const listStaleClaims = async (siteID: string): Promise<Claim[]> => {
  const data = await get<Claim[]>(
    'QBilling',
    `/resource/claim/stale${queryString({ siteID })}`
  );
  return data;
};

/**
 * @description Fetches count of claims that have been ready longer than 6 months by site ID.
 * @param siteID - The ID of the site the claims belong to.
 * @returns A promise that resolves to a number which is the count of stale claims for that site.
 */

const getStaleClaimCount = async ({
  siteID,
}: CountParams): Promise<StatusesCountResult> => {
  const data = await get<StatusesCountResult>(
    'QBilling',
    `/resource/claim/stale/count${queryString({ siteID })}`
  );
  return data;
};

/**
 * @description Fetches claims for a specific site.
 * @param siteID - The ID of the site.
 * @returns A promise that resolves to an array of `Claim` objects.
 */
const listClaimsBySite = async ({
  siteID,
}: {
  siteID: string;
}): Promise<Claim[]> =>
  await get<Claim[]>(
    'QBilling',
    `/resource/claim/site${queryString({ siteID })}`
  );

const listHospitalCodesByProvider = async ({
  providerID,
  siteID,
}: {
  providerID: string;
  siteID?: string;
}): Promise<string[]> =>
  await get<string[]>(
    'QBilling',
    `/resource/claim/hospital/provider${queryString({ providerID, siteID })}`
  );

/**
 * @description Fetches claims for a specific site and status.
 * @param siteID - The ID of the site.
 * @param status  - The status of the claims.
 * @returns A promise that resolves to an array of `Claim` objects.
 */
const listSiteClaimsByStatus = async ({
  siteID,
  status,
}: {
  siteID: string;
  status: string;
}): Promise<Claim[]> =>
  await get<Claim[]>(
    'QBilling',
    `/resource/claim/site/status${queryString({
      siteID,
      tariffStatus: status,
    })}`
  );

/**
 * @description Fetches claims for organization sites within a date range.
 * @param sites - An array of site IDs.
 * @param startDate - The start date of the range.
 * @param endDate - The end date of the range.
 * @used by dashboard claim chart
 * @returns A promise that resolves to an `OrganizationClaimsCounts` object.
 */
const listClaimsByOrganizationSitesInRange = async ({
  sites,
  startDate,
  endDate,
}: {
  sites: string[];
  startDate: string;
  endDate: string;
}): Promise<OrganizationClaimsCounts> =>
  await get<OrganizationClaimsCounts>(
    'QBilling',
    `/resource/claim/organization${queryString({ sites, startDate, endDate })}`
  );

const listClaimsByUserInRange = async ({
  userID,
  startDate,
  endDate,
}: {
  userID: string;
  startDate: string;
  endDate: string;
}): Promise<OrganizationClaimsCounts> =>
  await get<OrganizationClaimsCounts>(
    'QBilling',
    `/resource/claim/user${queryString({ userID, startDate, endDate })}`
  );

/**
 * @description Fetches paginated claims for a specific site.
 * @param siteID - The ID of the site.
 * @param page - The page number.
 * @param pageSize - The number of items per page.
 * @param filters - Optional filters to apply.
 * @param globalFilter - Optional global filter to apply.
 * @param sorting - Optional sorting parameters.
 * @returns A promise that resolves to a `ClaimPaginatedResult` object.
 */
const paginatedClaimsBySite = async ({
  siteID,
  page,
  pageSize,
  filters,
  globalFilter,
  sorting,
}: PaginationParams): Promise<ClaimPaginatedResult> => {
  const filterString = convertClaimFiltersToQueryString(filters);
  const globalFilterString = convertGlobalFilterToQueryString(globalFilter);
  const sortingString = convertSortingToQueryString(sorting);
  const claims = await get<ClaimPaginatedResult>(
    'QBilling',
    `/resource/claim/site/page${queryString({
      siteID,
      page,
      pageSize,
      filters: encodeURIComponent(filterString),
      globalFilter: encodeURIComponent(globalFilterString),
      sorting: sortingString,
    })}`
  );
  return claims;
};

/**
 * @description Fetches paginated claims for a specific site.
 * @param userID - The ID of the user of the provider.
 * @param page - The page number.
 * @param pageSize - The number of items per page.
 * @param filters - Optional filters to apply.
 * @param globalFilter - Optional global filter to apply.
 * @param sorting - Optional sorting parameters.
 * @returns A promise that resolves to a `ClaimPaginatedResult` object.
 */
const paginatedClaimsByUser = async ({
  userID,
  siteID,
  page,
  pageSize,
  filters,
  globalFilter,
  sorting,
}: PaginationParams): Promise<ClaimPaginatedResult> => {
  const filterString = convertClaimFiltersToQueryString(filters);
  const globalFilterString = convertGlobalFilterToQueryString(globalFilter);
  const sortingString = convertSortingToQueryString(sorting);
  const claims = await get<ClaimPaginatedResult>(
    'QBilling',
    `/resource/claim/user/page${queryString({
      userID,
      siteID,
      page,
      pageSize,
      filters: encodeURIComponent(filterString),
      globalFilter: encodeURIComponent(globalFilterString),
      sorting: sortingString,
    })}`
  );
  return claims;
};

/*
 * @description Fetches paginated claims for a specific patient.
 * @param patientID - The ID of the patient.
 * @param page - The page number.
 * @param pageSize - The number of items per page.
 * @param filters - Optional filters to apply.
 * @param globalFilter - Optional global filter to apply.
 * @param sorting - Optional sorting parameters.
 * @returns A promise that resolves to a `ClaimPaginatedResult` object.
 */

const paginatedClaimsByPatient = async ({
  patientID,
  siteID,
  page,
  pageSize,
  filters,
  globalFilter,
  sorting,
  timezone,
}: PaginationParams & { timezone: string }): Promise<ClaimPaginatedResult> => {
  const filterString = convertClaimFiltersToQueryString(filters);
  const globalFilterString = convertGlobalFilterToQueryString(globalFilter);
  const sortingString = convertSortingToQueryString(sorting);
  const claims = await get<ClaimPaginatedResult>(
    'QBilling',
    `/resource/claim/patient/page${queryString({
      patientID,
      siteID,
      page,
      pageSize,
      filters: encodeURIComponent(filterString),
      globalFilter: encodeURIComponent(globalFilterString),
      sorting: sortingString,
      timezone,
    })}`
  );
  return claims;
};

/**
 * @description Creates a new claim.
 * @param claim - The claim object to create.
 * @returns A promise that resolves to a `ClaimMutationResult`.
 */

const createClaim = async (
  claim: CreateClaimRequest
): Promise<ClaimMutationResult> => {
  const response = await post<
    ClaimMutationResponse | Claim,
    CreateClaimRequest
  >('QBilling', '/resource/claim', claim);

  if (!response) {
    const error = new NoDataFoundError(
      'No Data Found',
      'createClaim',
      '/resource/claim'
    );
    handleSentryErrors({
      error,
      displayNotification: false,
      functionName: 'createClaim',
      params: '/resource/claim',
    });
    throw error;
  }

  if (isClaimMutationResponse(response)) {
    return {
      ...response.claim,
      rules: response.rules,
    };
  }

  return response;
};

export const evaluateClaimRules = async (
  input: ClaimRuleEvaluationInput
): Promise<RuleEvaluationResult> => {
  const response = await post<RuleEvaluationResult, ClaimRuleEvaluationInput>(
    'QBilling',
    '/resource/claim/rules/evaluate',
    input
  );

  if (!response) {
    throw new NoDataFoundError(
      'No Data Found',
      'evaluateClaimRules',
      '/resource/claim/rules/evaluate'
    );
  }

  return response;
};

const bulkCreateClaims = async ({
  values,
  siteID,
}: {
  values: Claim[];
  siteID: string;
}): Promise<BulkClaimMutationResult> => {
  const response = await post<
    BulkClaimMutationResponse,
    {
      values: Claim[];
      siteID: string;
    }
  >('QBilling', '/resource/claim/bulk', {
    values,
    siteID,
  });

  if (!response) {
    const error = new NoDataFoundError(
      'No Data Found',
      'bulkCreateClaims',
      '/resource/claim/bulk'
    );
    handleSentryErrors({
      error,
      displayNotification: false,
      functionName: 'bulkCreateClaims',
      params: '/resource/claim/bulk',
    });
    throw error;
  }

  if (isBulkClaimMutationResponse(response)) {
    return response;
  }

  throw new Error('Invalid bulk claim create response.');
};

const listPreviousClaimBatches = async ({
  siteID,
  page = 1,
  pageSize = 25,
}: {
  siteID: string;
  page?: number;
  pageSize?: number;
}): Promise<ListClaimBatchesResponse> => {
  return await get<ListClaimBatchesResponse>(
    'QBilling',
    `/resource/claim/batches${queryString({ siteID, page, pageSize })}`
  );
};

const getPreviousClaimBatchDetails = async ({
  siteID,
  batchID,
}: {
  siteID: string;
  batchID: string;
}): Promise<ClaimBatchDetails> => {
  return await get<ClaimBatchDetails>(
    'QBilling',
    `/resource/claim/batches/details${queryString({ siteID, batchID })}`
  );
};

type BulkReconcileTariffsInput = ReconciliationComments & {
  claimID: string;
  debitIDs: string[];
  tariffIDs: string[];
};

type BulkReconcileTariffsRequest = BulkReconcileTariffsInput & {
  recordAdjustment: boolean;
};

const bulkReconcileTariffsApi = async ({
  values,
  recordAdjustment = false,
}: {
  values: BulkReconcileTariffsInput[];
  recordAdjustment?: boolean;
}): Promise<Claim[]> => {
  const payload = {
    values: values.map((value) => ({ ...value, recordAdjustment })),
  };
  const response = await post<
    Claim[],
    { values: BulkReconcileTariffsRequest[] }
  >('QBilling', '/resource/claim/reconcile/bulk', payload);

  if (!response) {
    const error = new NoDataFoundError(
      'No Data Found',
      'bulkReconcileTariffs',
      '/resource/claim/reconcile/bulk'
    );
    handleSentryErrors({
      error,
      displayNotification: false,
      functionName: 'bulkReconcileTariffs',
      params: '/resource/claim/reconcile/bulk',
    });
    throw error;
  }

  if (!Array.isArray(response)) {
    throw new Error('Invalid bulk tariff reconcile response.');
  }

  return response;
};

const bulkCancelClaimsApi = async ({
  claimIDs,
}: {
  claimIDs: string[];
}): Promise<string[]> => {
  const response = await post<string[], { claimIDs: string[] }>(
    'QBilling',
    '/resource/claim/cancel/bulk',
    { claimIDs }
  );

  if (!response || !Array.isArray(response)) {
    throw new Error('Invalid bulk claim cancellation response.');
  }

  return response;
};

const bulkEditClaimsApi = async (
  input: BulkClaimEditInput
): Promise<string[]> => {
  const response = await put<string[], BulkClaimEditInput>(
    'QBilling',
    '/resource/claim/bulk',
    input
  );

  return response;
};

/**
 * @description Updates a claim.
 * @param claim - The claim object to update.
 * @returns A promise that resolves to a `ClaimMutationResult`.
 */
const updateClaim = async (claim: Claim): Promise<ClaimMutationResult> => {
  const response = await put<ClaimMutationResponse | Claim, Claim>(
    'QBilling',
    '/resource/claim',
    claim
  );

  if (!response) {
    const error = new NoDataFoundError(
      'No Data Found',
      'updateClaim',
      '/resource/claim'
    );
    handleSentryErrors({
      error,
      displayNotification: false,
      functionName: 'updateClaim',
      params: '/resource/claim',
    });
    throw error;
  }

  if (isClaimMutationResponse(response)) {
    return {
      ...response.claim,
      rules: response.rules,
    };
  }

  return response;
};

/**
 * @description Fetches a claim by its ID.
 * @param id - The ID of the claim to retrieve.
 * @returns A promise that resolves to a `Claim` object or `null` if not found.
 */
export const useGetClaim = ({
  id,
  displayNotification = false,
}: {
  id: string;
  displayNotification?: boolean;
}): UseQueryResult<Claim, Error> => {
  return useGetQuery<Claim>({
    queryKey: claimQueryKeys.get(id),
    fetchFunction: getClaim,
    isEntity: isClaim,
    functionName: 'getClaim',
    enabled: !!id,
    params: id,
    displayNotification,
  });
};

export const prefetchClaimById = async (
  queryClient: QueryClient,
  id: string
) => {
  if (!id) return;
  await queryClient.prefetchQuery({
    queryKey: claimQueryKeys.get(id),
    queryFn: () => getClaim(id),
    staleTime: 60000 * 60 * 3, // 3 hours
  });
};

/**
 * @description Hook to fetch the history of a claim by its ID.
 * @param claimID - The ID of the claim.
 * @param displayNotification - Whether to display a notification if no data is found.
 * @returns A `UseQueryResult` containing an array of `ClaimHistory` objects or an error.
 */
export const useGetClaimHistory = ({
  claimID,
  displayNotification = false,
}: {
  claimID: string;
  displayNotification?: boolean;
}): UseQueryResult<ClaimHistory[], Error> => {
  return useListQuery<ClaimHistory>({
    queryKey: claimQueryKeys.getClaimHistory(claimID),
    fetchFunction: getClaimHistory,
    isEntity: isClaimHistory,
    functionName: 'getClaimHistory',
    enabled: !!claimID,
    params: claimID,
    displayNotification,
  });
};

export const useListPendingTariffResubmissions = (
  claimID: string
): UseQueryResult<TariffResubmissionRequest[], Error> => {
  return useListQuery<TariffResubmissionRequest>({
    queryKey: claimQueryKeys.getPendingTariffResubmissions(claimID),
    fetchFunction: listPendingTariffResubmissions,
    isEntity: (obj: any): obj is TariffResubmissionRequest => !!obj,
    functionName: 'listPendingTariffResubmissions',
    enabled: !!claimID,
    params: { claimID },
    displayNotification: false,
    validationSchema: tariffResubmissionRequestSchema,
  });
};

/**
 * @description Hook to fetch the count of claim statuses for a specific site.
 * @param siteID - The ID of the site.
 * @param filters - Optional filters to apply.
 * @returns A `UseQueryResult` containing a `CountReturnTypes` object or an error.
 */
export const useGetSiteClaimStatusesCount = (
  siteID: string
): UseQueryResult<CountReturnTypes, Error> => {
  return useCountQuery({
    queryKey: claimQueryKeys.getSiteClaimStatusesCount(siteID),
    functionName: 'getSiteClaimStatusesCount',
    enabled: !!siteID,
    params: { siteID },
    fetchFunction: getSiteClaimStatusesCount,
  });
};

/**
 * @description Hook to fetch the count of claim statuses for a specific user.
 * @param userID - The ID of the user.
 * @param filters - Optional filters to apply.
 * @returns A `UseQueryResult` containing a `CountReturnTypes` object or an error.
 */
export const useGetUserClaimStatusesCount = (
  userID: string,
  siteID: string,
  filters?: MRT_ColumnFiltersState
): UseQueryResult<CountReturnTypes, Error> => {
  return useCountQuery({
    queryKey: claimQueryKeys.getUserClaimStatusesCount(userID, siteID, filters),
    functionName: 'getUserClaimStatusesCount',
    enabled: !!userID && hasSiteID(siteID),
    params: { userID, siteID, filters },
    fetchFunction: getUserClaimStatusesCount,
  });
};

/**
 * @description Hook to fetch claim tariff status counts for a specific site.
 * @param siteID - The ID of the site.
 * @returns A `UseQueryResult` containing a `CountReturnTypes` object or an error.
 */
export const useGetSiteClaimTariffStatusesCount = (
  siteID: string,
  filters?: MRT_ColumnFiltersState,
  globalFilter?: string
): UseQueryResult<CountReturnTypes, Error> => {
  return useCountQuery({
    queryKey: claimQueryKeys.getSiteClaimTariffStatusesCountWithFilters(
      siteID,
      filters,
      globalFilter
    ),
    functionName: 'getSiteClaimTariffStatusesCount',
    enabled: !!siteID,
    params: { siteID, filters, globalFilter },
    fetchFunction: getSiteClaimTariffStatusesCount,
  });
};

/**
 * @description Hook to fetch claim tariff status counts for a specific user and site.
 * @param userID - The ID of the user.
 * @param siteID - The ID of the site.
 * @returns A `UseQueryResult` containing a `CountReturnTypes` object or an error.
 */
export const useGetUserClaimTariffStatusesCount = (
  userID: string,
  siteID: string | string[],
  filters?: MRT_ColumnFiltersState,
  globalFilter?: string
): UseQueryResult<CountReturnTypes, Error> => {
  return useCountQuery({
    queryKey: claimQueryKeys.getUserClaimTariffStatusesCountWithFilters(
      userID,
      siteID,
      filters,
      globalFilter
    ),
    functionName: 'getUserClaimTariffStatusesCount',
    enabled: !!userID,
    params: { userID, siteID, filters, globalFilter },
    fetchFunction: getUserClaimTariffStatusesCount,
  });
};

/**
 * @description Hook to fetch the count of a specific claim status for a site.
 * @param siteID - The ID of the site.
 * @param tariffStatus - The tariff status to count.
 * @param filters - Optional filters to apply.
 * @returns A `UseQueryResult` containing a `CountReturnTypes` object or an error.
 */
export const useGetSiteClaimStatusCount = (
  siteID: string,
  tariffStatus: ClaimTariffStatusValue,
  filters?: MRT_ColumnFiltersState
): UseQueryResult<CountReturnTypes, Error> => {
  return useCountQuery({
    queryKey: claimQueryKeys.getSiteClaimStatusCount(
      siteID,
      tariffStatus,
      filters
    ),
    functionName: 'getSiteClaimStatusCount',
    enabled: !!siteID && !!tariffStatus,
    params: { siteID, tariffStatus, filters },
    fetchFunction: getSiteClaimStatusCount,
  });
};

/**
 * Hook to fetch all claims.
 * @param displayNotification - Whether to display a notification if no data is found.
 * @returns A `UseQueryResult` containing an array of `Claim` objects or an error.
 */
export const useListAllClaims = ({
  displayNotification = false,
}: {
  displayNotification?: boolean;
}): UseQueryResult<Claim[], Error> => {
  return useListQuery<Claim>({
    queryKey: claimQueryKeys.list(),
    fetchFunction: listClaims,
    isEntity: isClaim,
    functionName: 'listClaims',
    enabled: true,
    displayNotification: displayNotification,
  });
};

/**
 * @description Hook to fetch claims for a specific site.
 * @param siteID - The ID of the site.
 * @param displayNotification - Whether to display a notification if no data is found.
 * @returns A `UseQueryResult` containing an array of `Claim` objects or an error.
 */
export const useListClaimsBySite = ({
  siteID,
  displayNotification = false,
}: {
  siteID: string;
  displayNotification?: boolean;
}): UseQueryResult<Claim[], Error> => {
  return useListQuery<Claim>({
    queryKey: claimQueryKeys.listBySite(siteID),
    fetchFunction: listClaimsBySite,
    isEntity: isClaim,
    functionName: 'listClaimsBySite',
    enabled: !!siteID,
    params: siteID,
    displayNotification,
  });
};

/**
 * @description Hook to fetch hospital codes for a specific provider.
 * @param providerID - The ID of the provider.
 * @param siteID - The ID of the site (optional).
 * @returns A `UseQueryResult` containing an array of hospital code strings or an error.
 */
export const useListHospitalCodesByProvider = ({
  providerID,
  siteID,
}: {
  providerID: string;
  siteID?: string;
}): UseQueryResult<string[], Error> => {
  return useQuery<string[], Error>({
    queryKey: ['claim', 'providerHospitalCodes', providerID, siteID ?? null],
    queryFn: () => listHospitalCodesByProvider({ providerID, siteID }),
    enabled: !!providerID,
    retry: false,
  });
};

/**
 * @description Hook to fetch claims for organization sites within a date range.
 * @param sites - An array of site IDs.
 * @param startDate - The start date of the range.
 * @param endDate - The end date of the range.
 * @returns A `UseQueryResult` containing an `OrganizationClaimsCounts` object or an error.
 */
export const useListClaimsByOrganizationSitesInRange = ({
  sites = [],
  startDate,
  endDate,
}: {
  sites: string[];
  startDate: string;
  endDate: string;
}): UseQueryResult<OrganizationClaimsCounts, Error> => {
  return useCountOrganizationClaimsQuery({
    queryKey: claimQueryKeys.listClaimsByOrganizationSitesInRange(
      sites,
      startDate,
      endDate
    ),
    enabled: !!sites?.length && !!startDate && !!endDate,
    fetchFunction: listClaimsByOrganizationSitesInRange,
    functionName: 'listClaimsByOrganizationSitesInRange',
    params: { sites, startDate, endDate },
  });
};

export const useListClaimsByUserInRange = (
  userID: string,
  startDate: string,
  endDate: string
): UseQueryResult<OrganizationClaimsCounts, Error> => {
  return useCountOrganizationClaimsQuery({
    queryKey: claimQueryKeys.listClaimsByOrganizationSitesInRange(
      [userID],
      startDate,
      endDate
    ),
    enabled: !!userID && !!startDate && !!endDate,
    fetchFunction: listClaimsByUserInRange,
    functionName: 'listClaimsByUserInRange',
    params: { userID, startDate, endDate },
  });
};

/**
 * @description Hook to fetch claims for a specific site and status.
 * @param siteID - The ID of the site.
 * @param status - The status of the claims.
 * @param displayNotifications - Whether to display a notification if no data is found.
 * @returns A `UseQueryResult` containing an array of `Claim` objects or an error.
 */

export const useListSiteClaimsByStatus = ({
  siteID,
  status,
  displayNotifications = true,
}: {
  siteID: string;
  status: string;
  displayNotifications?: boolean;
}): UseQueryResult<Claim[], Error> => {
  return useListQuery<Claim>({
    queryKey: claimQueryKeys.listSiteClaimsByStatus(siteID, status),
    fetchFunction: listSiteClaimsByStatus,
    isEntity: isClaim,
    functionName: 'listSiteClaimsByStatus',
    enabled: !!siteID,
    params: { siteID, status },
    displayNotification: displayNotifications,
  });
};

/**
 * @description Hook to fetch claims based on admin status and site ID.
 * @param isAdmin - Whether the user is an admin.
 * @param siteID - The ID of the site.
 * @returns A `UseQueryResult` containing an array of `Claim` objects or an error.
 */

export const useListClaims = (
  isAdmin: boolean,
  siteID: string
): UseQueryResult<Claim[], Error> => {
  return useListQuery<Claim>({
    queryKey: claimQueryKeys.listAllClaims(isAdmin, siteID),
    fetchFunction: isAdmin ? listClaims : listClaimsBySite,
    isEntity: isClaim,
    functionName: 'listClaims',
    enabled: isAdmin ? true : !!siteID,
    params: siteID,
  });
};

export const useListClaimsForCSV = (
  status: string | undefined,
  siteID: string,
  enabled: boolean
): UseQueryResult<Claim[], Error> => {
  const hasStatus = !!status && status.trim() !== '';
  return useListQuery<Claim>({
    queryKey: claimQueryKeys.csvClaims(siteID, status),
    fetchFunction: hasStatus ? listSiteClaimsByStatus : listClaimsBySite,
    isEntity: isClaim,
    functionName: hasStatus ? 'listSiteClaimsByStatus' : 'listBySite',
    enabled: !!siteID && !!enabled,
    params: hasStatus ? { siteID, status } : { siteID },
  });
};

/**
 * @description Hook to fetch "stale" claims (claims that have been ready longer than 6 months) by site ID.
 * @param siteID - The ID of the site the claims belong to.
 * @returns A `UseQueryResult` containing an array of `Claim` objects or an error.
 */

export const useListStaleClaims = (
  siteID: string
): UseQueryResult<Claim[], Error> => {
  return useListQuery<Claim>({
    queryKey: claimQueryKeys.listStaleClaims(siteID),
    fetchFunction: listStaleClaims,
    isEntity: isClaim,
    functionName: 'listStaleClaims',
    enabled: !!siteID,
    params: siteID,
  });
};

/**
 * @description Hook to fetch "stale" claims (claims that have been ready longer than 6 months) by site ID.
 * @param siteID - The ID of the site the claims belong to.
 * @returns A `UseQueryResult` containing an array of `Claim` objects or an error.
 */

export const useGetStaleClaimCount = (
  siteID: string
): UseQueryResult<CountReturnTypes, Error> => {
  return useCountQuery({
    queryKey: claimQueryKeys.listStaleClaims(siteID),
    functionName: 'getStaleClaimCount',
    enabled: !!siteID,
    params: { siteID },
    fetchFunction: getStaleClaimCount,
  });
};

/**
 * @description Hook to fetch paginated claims for a specific site.
 * @param siteID - The ID of the site.
 * @param page - The page number.
 * @param pageSize - The number of items per page.
 * @param filters - Optional filters to apply.
 * @param globalFilter - Optional global filter to apply.
 * @param sorting - Optional sorting parameters.
 * @returns A `UseQueryResult` containing a `ClaimPaginatedResult` object or an error.
 */
export const usePaginateClaimsBySite = (
  siteID: string,
  page: number,
  pageSize: number,
  filters?: MRT_ColumnFiltersState,
  globalFilter?: string,
  sorting?: MRT_SortingState
): UseQueryResult<ClaimPaginatedResult, Error> => {
  const functionName = 'paginatedClaimsBySite';

  const queryKey = claimQueryKeys.paginatedClaimsBySite(
    siteID,
    page,
    pageSize,
    filters,
    globalFilter,
    sorting
  );

  const enabled = !!siteID;

  return usePaginatedQuery<ClaimList, ClaimPaginatedResult>({
    queryKey: queryKey,
    isEntity: isClaimList,
    functionName,
    params: {
      siteID,
      page,
      pageSize,
      filters,
      globalFilter,
      sorting,
    },
    fetchFunction: paginatedClaimsBySite,
    enabled: enabled,
    validationSchema: claimListSchema,
  });
};

/**
 * @description Hook to fetch paginated claims for a specific site.
 * @param userID - The ID of the user for the provider.
 * @param page - The page number.
 * @param pageSize - The number of items per page.
 * @param filters - Optional filters to apply.
 * @param globalFilter - Optional global filter to apply.
 * @param sorting - Optional sorting parameters.
 * @returns A `UseQueryResult` containing a `ClaimPaginatedResult` object or an error.
 */
export const usePaginateClaimsByUser = (
  userID: string,
  siteID: string | string[],
  page: number,
  pageSize: number,
  filters?: MRT_ColumnFiltersState,
  globalFilter?: string,
  sorting?: MRT_SortingState
): UseQueryResult<ClaimPaginatedResult, Error> => {
  const functionName = 'paginatedClaimsByUser';
  const queryKey = claimQueryKeys.paginatedClaimsByUser(
    userID,
    siteID,
    page,
    pageSize,
    filters,
    globalFilter,
    sorting
  );
  const enabled = !!userID && hasSiteID(siteID);
  return usePaginatedQuery<ClaimList, ClaimPaginatedResult>({
    queryKey: queryKey,
    isEntity: isClaimList,
    functionName,
    params: {
      userID,
      siteID,
      page,
      pageSize,
      filters,
      globalFilter,
      sorting,
    },
    fetchFunction: paginatedClaimsByUser,
    enabled: enabled,
    validationSchema: claimListSchema,
  });
};

export const usePaginateClaimsByPatient = (
  patientID: string,
  siteID: string,
  page: number,
  pageSize: number,
  timezone: string,
  filters?: MRT_ColumnFiltersState,
  globalFilter?: string,
  sorting?: MRT_SortingState
): UseQueryResult<ClaimPaginatedResult, Error> => {
  const functionName = 'paginatedClaimsByPatient';
  const queryKey = claimQueryKeys.paginatedClaimsByPatient(
    patientID,
    siteID,
    page,
    pageSize,
    filters,
    globalFilter,
    sorting,
    timezone
  );

  const enabled = !!patientID && !!siteID;
  return usePaginatedQuery<ClaimList, ClaimPaginatedResult>({
    queryKey: queryKey,
    isEntity: isClaimList,
    functionName,
    params: {
      patientID,
      siteID,
      page,
      pageSize,
      filters,
      globalFilter,
      sorting,
      timezone,
    } as PaginationParams,
    fetchFunction: paginatedClaimsByPatient,
    enabled: enabled,
    validationSchema: claimListSchema,
  });
};

/**
 * @description Hook to prefetch paginated claims for a specific site.
 * @param queryClient - The query client instance.
 * @param siteID - The ID of the site.
 * @param page - The page number.
 * @param pageSize - The number of items per page.
 * @param filters - Optional filters to apply.
 * @param globalFilter - Optional global filter to apply.
 * @param sorting - Optional sorting parameters.
 */
export const prefetchPaginateClaimsBySite = async (
  queryClient: QueryClient,
  siteID: string,
  page: number,
  pageSize: number,
  filters?: MRT_ColumnFiltersState,
  globalFilter?: string,
  sorting?: MRT_SortingState
) => {
  const functionName = 'paginatedClaimsBySite';

  const queryKey = claimQueryKeys.paginatedClaimsBySite(
    siteID,
    page,
    pageSize,
    filters,
    globalFilter,
    sorting
  );

  await prefetchQuery<ClaimList, ClaimPaginatedResult>({
    queryClient,
    queryKey: queryKey,
    isEntity: isClaimList,
    functionName,
    params: {
      siteID,
      page,
      pageSize,
      filters,
      globalFilter,
      sorting,
    },
    fetchFunction: paginatedClaimsBySite,
    displayNotification: false,
    validationSchema: claimListSchema,
    enabled: !!siteID,
  });
};

export const usePreFetchPaginateClaimsBySite = prefetchPaginateClaimsBySite;

/**
 * @description Hook to prefetch paginated claims for a specific site.
 * @param queryClient - The query client instance.
 * @param userID - The ID of the providers user.
 * @param page - The page number.
 * @param pageSize - The number of items per page.
 * @param filters - Optional filters to apply.
 * @param globalFilter - Optional global filter to apply.
 * @param sorting - Optional sorting parameters.
 */
export const prefetchPaginateClaimsByUser = async (
  queryClient: QueryClient,
  userID: string,
  siteID: string | string[],
  page: number,
  pageSize: number,
  filters?: MRT_ColumnFiltersState,
  globalFilter?: string,
  sorting?: MRT_SortingState
) => {
  const functionName = 'paginatedClaimsByUser';

  const queryKey = claimQueryKeys.paginatedClaimsByUser(
    userID,
    siteID,
    page,
    pageSize,
    filters,
    globalFilter,
    sorting
  );

  await prefetchQuery<ClaimList, ClaimPaginatedResult>({
    queryClient,
    queryKey: queryKey,
    isEntity: isClaimList,
    functionName,
    params: {
      userID,
      siteID,
      page,
      pageSize,
      filters,
      globalFilter,
      sorting,
    },
    fetchFunction: paginatedClaimsByUser,
    displayNotification: false,
    validationSchema: claimListSchema,
    enabled: !!userID && hasSiteID(siteID),
  });
};

export const usePreFetchPaginateClaimsByUser = prefetchPaginateClaimsByUser;

/**
 * Hook to create a new claim.
 * @returns A `UseMutationResult` containing a `ClaimMutationResult` or an error.
 */
export const useCreateClaim = (): UseMutationResult<
  ClaimMutationResult,
  Error
> => {
  return useMutationQuery<ClaimMutationResult>({
    errorMessage: 'Claim Creation Failed',
    mutationFn: createClaim,
    successMessage: 'Claim Created',
    queryKey: claimQueryKeys.all,
  });
};

export const useBulkCreateClaims = (): UseMutationResult<
  BulkClaimMutationResult,
  Error
> => {
  return useMutationQuery<BulkClaimMutationResult>({
    mutationFn: async ({ siteID, values }) =>
      bulkCreateClaims({ values, siteID }),
    successMessage: 'Claims Created',
    errorMessage: 'Claim Creation Failed',
    queryKey: claimQueryKeys.all,
  });
};

export const useListPreviousClaimBatches = ({
  siteID,
  page = 1,
  pageSize = 25,
}: {
  siteID: string;
  page?: number;
  pageSize?: number;
}): UseQueryResult<ListClaimBatchesResponse, Error> => {
  return useQuery<ListClaimBatchesResponse, Error>({
    queryKey: claimQueryKeys.previousBatches(siteID, page, pageSize),
    queryFn: () => listPreviousClaimBatches({ siteID, page, pageSize }),
    enabled: !!siteID,
    retry: false,
  });
};

export const useGetPreviousClaimBatchDetails = ({
  siteID,
  batchID,
  enabled = true,
}: {
  siteID: string;
  batchID: string;
  enabled?: boolean;
}): UseQueryResult<ClaimBatchDetails, Error> => {
  return useQuery<ClaimBatchDetails, Error>({
    queryKey: claimQueryKeys.previousBatchDetails(siteID, batchID),
    queryFn: () => getPreviousClaimBatchDetails({ siteID, batchID }),
    enabled: !!siteID && !!batchID && enabled,
    retry: false,
  });
};

// Hook for bulk tariff reconciliation / update in a single request.
export const useBulkReconcileTariffs = (): UseMutationResult<
  Claim[],
  Error
> => {
  return useMutationQuery<Claim[]>({
    mutationFn: async ({
      values,
      recordAdjustment,
    }: {
      values: BulkReconcileTariffsInput[];
      recordAdjustment?: boolean;
    }) => bulkReconcileTariffsApi({ values, recordAdjustment }),
    successMessage: 'Tariffs Reconciled',
    errorMessage: 'Tariff Reconciliation Failed',
    queryKey: claimQueryKeys.bulkReconcile(),
  });
};

export const useBulkCancelClaims = (): UseMutationResult<string[], Error> => {
  return useMutationQuery<string[]>({
    mutationFn: ({ claimIDs }: { claimIDs: string[] }) =>
      bulkCancelClaimsApi({ claimIDs }),
    successMessage: 'Claims Cancelled',
    errorMessage: 'Claim Cancellation Failed',
    queryKey: claimQueryKeys.all,
  });
};

export const useBulkEditClaims = (): UseMutationResult<string[], Error> => {
  return useMutationQuery<string[]>({
    mutationFn: bulkEditClaimsApi,
    successMessage: 'Claims Updated',
    errorMessage: 'Claim Bulk Edit Failed',
    queryKey: claimQueryKeys.all,
  });
};

/**
 * Hook to update an existing claim.
 * @returns A `UseMutationResult` containing a `ClaimMutationResult` or an error.
 */
export const useUpdateClaim = (): UseMutationResult<
  ClaimMutationResult,
  Error
> => {
  return useMutationQuery<ClaimMutationResult>({
    mutationFn: updateClaim,
    successMessage: 'Claim Updated',
    errorMessage: 'Claim Update Failed',
    queryKey: claimQueryKeys.all,
    mutationKey: ['updateClaim'],
    invalidateQueryKeys: (data, variables?: Claim) => {
      const claim = (data as ClaimWithRulesResult)?.rules
        ? (data as ClaimWithRulesResult)
        : (data as Claim);
      const claimID = claim?.id ?? variables?.id;
      const siteID = claim?.siteID ?? variables?.siteID;
      return [
        claimID ? claimQueryKeys.get(claimID) : [],
        claimID ? claimQueryKeys.getClaimHistory(claimID) : [],
        siteID ? claimQueryKeys.listBySite(siteID) : [],
        siteID
          ? claimQueryKeys.getSiteClaimTariffStatusesCountWithFilters(siteID)
          : [],
        ['claim', 'userClaimTariffStatusesCount'],
      ].filter((queryKey) => queryKey.length > 0);
    },
  });
};
